import type { ReactNode } from 'react';
import { useIsNarrow } from '@/lib/useMediaQuery';
import { color, font, layout, radius } from '@/lib/theme';
// The full brand mark — gear behind the wording — same import Dashboard.tsx
// and LoginScreen.tsx already use. Do NOT also spell out "Allied Automotive"
// in text next to it (design spec): the wordmark is baked into the image.
import fullLogo from '@/assets/allied-full-logo.png';

/**
 * Direction D ("Sidebar + Brand") shell — a persistent left sidebar on
 * desktop, a fixed bottom tab bar on narrow viewports. Presentational only:
 * no data fetching, no knowledge of Supabase or any view union. A caller
 * hands it a flat nav item list and tells it which one is active; everything
 * else (what the items are, whether a given manager should see one) is the
 * caller's decision.
 *
 * Nav items are real `<a href>` elements, not buttons with onClick — the
 * app's hash router (App.tsx / Dashboard.tsx's own `hashchange` listeners)
 * already re-derives the current view from `window.location.hash` on every
 * such click, so plain anchors get native hash navigation, middle-click-to-
 * new-tab, and back/forward for free with zero event wiring here.
 *
 * The content slot is a real `<main>` (both branches below), not a `<div>`
 * — an accessibility-review finding once this shell covered every
 * signed-in view but Manager Home rather than just Dashboard: a `<div>`
 * gives a screen-reader landmark-navigation user nothing to jump past the
 * sidebar/tab-bar to. Safe as the page's only `<main>` — App.tsx renders no
 * `<main>` of its own around this shell.
 */
export interface AppShellNavItem {
  key: string;
  label: string;
  icon: ReactNode;
  href: string;
}

export function AppShell({
  items,
  activeKey,
  logoHref = '#/',
  userName,
  userInitials,
  isAdmin,
  onSignOut,
  signingOut,
  children,
}: {
  items: AppShellNavItem[];
  activeKey: string | null;
  /** Where the logo lockup links — Dashboard, the landing view now that
      Manager Home (a separate card-grid front door) has been retired. */
  logoHref?: string;
  userName: string;
  userInitials: string;
  isAdmin?: boolean;
  onSignOut: () => void;
  signingOut: boolean;
  children: ReactNode;
}) {
  const narrow = useIsNarrow();

  if (narrow) {
    return (
      <div style={S.narrowRoot}>
        {/* A slim identity bar — logo + avatar + sign out — scrolls away with
            the content, same as the approved mobile mockup's own top bar.
            Not part of the fixed chrome: the fixed tab bar below is. */}
        <div style={S.narrowTopBar}>
          <a href={logoHref} title="Dashboard" style={S.narrowLogoLink}>
            <img src={fullLogo} alt="Allied Automotive Service" style={S.narrowLogoImg} />
          </a>
          <div style={S.narrowUser}>
            <span
              style={S.avatar}
              title={`${userName}${isAdmin ? ' (admin)' : ''}`}
            >
              {userInitials}
            </span>
            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              style={S.narrowSignOut}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>

        {/* Bottom padding reserves room for the fixed tab bar below, so the
            last bit of real content never renders underneath it. */}
        <main style={S.narrowContent}>{children}</main>

        <nav aria-label="Primary" style={S.tabBar}>
          {items.map((item) => {
            const active = item.key === activeKey;
            return (
              <a
                key={item.key}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                style={{ ...S.tabItem, color: active ? color.brandBlue : color.textFaint }}
              >
                <span aria-hidden="true">{item.icon}</span>
                <span style={S.tabLabel}>{item.label}</span>
              </a>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div style={S.desktopRoot}>
      <nav aria-label="Primary" style={S.sidebar}>
        <a href={logoHref} title="Dashboard" style={S.logoLockup}>
          <img src={fullLogo} alt="Allied Automotive Service" style={S.logoImg} />
          <span style={S.logoLabel}>
            PTO
            <br />
            Portal
          </span>
        </a>

        <div style={S.navList}>
          {items.map((item) => {
            const active = item.key === activeKey;
            return (
              <a
                key={item.key}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                style={active ? { ...S.navItem, ...S.navItemActive } : S.navItem}
              >
                <span aria-hidden="true" style={S.navIcon}>
                  {item.icon}
                </span>
                {item.label}
              </a>
            );
          })}
        </div>

        <div style={S.userChip}>
          <span style={S.avatar}>{userInitials}</span>
          <span style={S.userName}>
            {userName}
            {isAdmin && <span style={S.adminTag}>admin</span>}
          </span>
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            style={S.signOutBtn}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </nav>

      <main style={S.main}>{children}</main>
    </div>
  );
}

const S = {
  desktopRoot: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: '100vh',
    background: color.pageBg,
  },
  sidebar: {
    width: layout.sidebarWidth,
    flex: `0 0 ${layout.sidebarWidth}`,
    boxSizing: 'border-box',
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    height: '100vh',
    overflowY: 'auto',
    background: color.cardBg,
    borderRight: `1px solid ${color.cardBorder}`,
    padding: '22px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  logoLockup: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '0 6px 18px',
    marginBottom: '14px',
    borderBottom: `1px solid ${color.cardBorder}`,
    textDecoration: 'none',
  },
  logoImg: { height: '34px', width: 'auto', display: 'block' },
  logoLabel: {
    borderLeft: `1px solid ${color.cardBorder}`,
    paddingLeft: '11px',
    fontSize: '9.5px',
    fontWeight: 700,
    color: color.textFaint,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    lineHeight: 1.3,
  },
  navList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '11px 14px',
    borderRadius: radius.pill,
    color: color.textSubtle,
    fontSize: '13.5px',
    fontWeight: 600,
    textDecoration: 'none',
    fontFamily: font.body,
  },
  navItemActive: {
    background: color.brandBlue,
    color: '#fff',
    fontFamily: font.display,
  },
  navIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  userChip: {
    marginTop: 'auto',
    paddingTop: '16px',
    borderTop: `1px solid ${color.cardBorder}`,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: radius.pill,
    // A11y fix (adversarial review, 2026-09-04): white-on-color.amber was
    // ~3.19:1 — below WCAG AA's 4.5:1 for this 12px bold text (too small for
    // the "large text" 3:1 exception). amberDark keeps the same accent
    // family at ~7.1:1.
    background: color.amberDark,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: font.display,
  },
  userName: {
    fontSize: '13px',
    fontWeight: 600,
    color: color.ink,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  adminTag: {
    marginLeft: '0.4rem',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: color.textFaint,
  },
  signOutBtn: {
    marginLeft: 'auto',
    flexShrink: 0,
    // Touch-target fix (adversarial review, 2026-09-04): matches the app's
    // own 2.75rem/44px convention (navBtn, todayBtn, retryBtn, closeBtn…).
    // Lower priority than the mobile equivalent since this one is
    // mouse-driven, but trivial to bring in line.
    minHeight: '2.75rem',
    padding: '0 4px',
    display: 'inline-flex',
    alignItems: 'center',
    border: 0,
    background: 'none',
    fontSize: '11.5px',
    color: color.textFaint,
    cursor: 'pointer',
    fontFamily: font.body,
  },
  main: { flex: '1 1 auto', minWidth: 0 },

  // ---- Narrow (bottom tab bar) ----
  narrowRoot: { minHeight: '100vh', background: color.pageBg },
  narrowTopBar: {
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    background: color.cardBg,
    borderBottom: `1px solid ${color.cardBorder}`,
  },
  narrowLogoLink: { display: 'flex', alignItems: 'center' },
  narrowLogoImg: { height: '26px', width: 'auto', display: 'block' },
  narrowUser: { display: 'flex', alignItems: 'center', gap: '10px' },
  narrowSignOut: {
    // Touch-target fix (adversarial review, 2026-09-04): was padding:0 with
    // no minHeight, well under the app's own 2.75rem/44px minimum — on the
    // one persistent control that's touch-driven by definition.
    minHeight: '2.75rem',
    padding: '0 4px',
    display: 'inline-flex',
    alignItems: 'center',
    border: 0,
    background: 'none',
    fontSize: '11.5px',
    fontWeight: 600,
    color: color.textFaint,
    cursor: 'pointer',
    fontFamily: font.body,
  },
  narrowContent: { paddingBottom: layout.bottomTabHeight },
  tabBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    height: layout.bottomTabHeight,
    background: color.cardBg,
    borderTop: `1px solid ${color.cardBorder}`,
    display: 'flex',
    alignItems: 'stretch',
    zIndex: 10,
  },
  tabItem: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    textDecoration: 'none',
    fontFamily: font.body,
  },
  tabLabel: { fontSize: '10px', fontWeight: 700 },
} satisfies Record<string, React.CSSProperties>;
